import { useLocation, useNavigate } from 'react-router-dom';
import { type SyntheticEvent, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  Grid,
  Paper,
  Divider,
  Container,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TuneIcon from '@mui/icons-material/Tune';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TagIcon from '@mui/icons-material/Tag';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { isDarkTheme } from 'utils/theme';

import { Header } from './Header';
import CustomDateRangePicker from 'components/DatePIcker/CustomDateRangePicker';
import AssetHandler from 'handlers/asset.handler';
import dayjs from 'dayjs';
import {
  assetTypeMappings,
  getAssetType,
} from '../FleetOverview/helpers/asset.helpers';
import type { Asset } from 'types/asset.types';
import { CompareAssetModal } from '../FleetOverview/components/AssetCardsV2/modals/CompareModal';
import {
  clearAssetComparisonByTags,
  selectAssociatedAssets,
} from 'store/asset.slice';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { ComparisonMetrics } from './ComparisonMetrics';
import { SingleFadeLoader } from 'components/SingleFadeLoader';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _dark = clientThemePalette[CLIENT_THEME].dark;
const _light = clientThemePalette[CLIENT_THEME].light;

export const FleetComparisonNew = () => {
  const { t } = useTranslation();
  const assetHandler = new AssetHandler();
  const location = useLocation();
  const navigate = useNavigate();
  const { assets } = location.state || [];
  const dispatch = useAppDispatch();

  const assetsComparison = useAppSelector(
    (state) => state.assetReducer.assetComparison
  );

  const unfilteredAssets = useAppSelector(selectAssociatedAssets);

  const theme = useAppSelector((state) => state.authReducer).customer.theme;
  const { thresholds, defaultThresholds } = useAppSelector(
    (state) => state.tagThresholdReducer
  );
  const enableDarkTheme = isDarkTheme(theme);
  const palette = enableDarkTheme ? _dark : _light;

  const [metricsType, setMetricsType] = useState('table');
  const [selectedStartDate, setSelectedStartDate] = useState(
    new Date(new Date().getTime() - 7 * 24 * 60 * 60 * 1000)
  );
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);

  const handleDateChange = (range: [Date | null, Date | null]) => {
    if (range[0] && range[1]) {
      setSelectedStartDate(range[0]);
      setSelectedEndDate(range[1]);
    }
  };

  // const handleButtonClick = (type: string) => {
  //   setMetricsType(type);
  // };

  // Handler to remove an asset from comparison
  const handleRemoveAsset = (assetId: string) => {
    // Filter out the asset with the matching ID
    const updatedAssets = assets.filter(
      (asset: Asset) => asset.assetId !== assetId
    );

    // Navigate to the same page with updated assets to force a refresh
    if (updatedAssets.length > 0) {
      navigate('/compare-assets', { state: { assets: updatedAssets } });
    } else {
      // If no assets left, redirect back to fleet overview
      navigate('/fleet-overview');
    }
  };

  // Handler to edit/replace an asset
  const handleEditAsset = (assetId: string) => {
    // Store the index of the asset to be replaced for the modal
    localStorage.setItem(
      'replaceAssetIndex',
      assets.findIndex((asset: Asset) => asset.assetId === assetId).toString()
    );
    setShowModal(true);
  };

  const getAssetComparison = async (
    startDate: string,
    endDate: string,
    deviceIds: string[],
    assetType: string
  ) => {
    await assetHandler.getAssetsComparisonData(
      startDate,
      endDate,
      deviceIds,
      assetType
    );
  };

  const formatParams = () => {
    const startDate = dayjs(selectedStartDate).format('YYYY-MM-DD');
    const endDate = dayjs(selectedEndDate).format('YYYY-MM-DD');
    const defaultAssetType = getAssetType(
      assetTypeMappings[assets[0].assetType]
    );
    const deviceIds = assets.map((asset: Asset) => asset.device.deviceId);
    return {
      startDate,
      endDate,
      defaultAssetType,
      deviceIds,
    };
  };

  useEffect(() => {
    if (assets?.length > 0 && selectedStartDate && selectedEndDate) {
      const { startDate, endDate, defaultAssetType, deviceIds } =
        formatParams();
      void getAssetComparison(startDate, endDate, deviceIds, defaultAssetType);
    }
  }, [assets, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    return () => {
      dispatch(clearAssetComparisonByTags());
    };
  }, [location, dispatch]);

  const selectedAssetIds = assets?.map((asset: Asset) => asset.assetId);
  const convertCamelToSnakeCase = (input: string): string => {
    return input.replace(/([A-Z])/g, (match, p1, offset) => {
      if (offset > 0 && input[offset - 1] !== '_') {
        return `_${p1.toLowerCase()}`;
      }
      return p1.toLowerCase();
    });
  };

  const tagsDropdown = assetsComparison.deviceTags.map((tag) => ({
    label: tag.tagAlias,
    id: tag.tagName,
  }));

  const handleSelect = (event: SyntheticEvent<Element, Event>, value: any) => {
    if (!value?.id) return;
    const { startDate, endDate, defaultAssetType, deviceIds } = formatParams();
    void assetHandler.getAssetsComparisonByTagsData(
      startDate,
      endDate,
      deviceIds,
      defaultAssetType,
      value?.id
    );
  };

  const reversedInput = [...assetsComparison.tags].reverse();
  // Create a mapping of tag IDs to their labels
  const tagIdToLabelMap: Record<string, string> = tagsDropdown.reduce<
    Record<string, string>
  >((map, tag) => {
    map[tag.id] = tag.label;
    return map;
  }, {});

  const tagMetrics: any = reversedInput.reduce((acc, item) => {
    return { ...acc, ...item.metrics };
  }, {});

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {showModal && (
        <CompareAssetModal
          key="compare-assets-modal-comparison"
          title={t('compare_assets')}
          handleAbort={() => {
            setShowModal(false);
          }}
          open={showModal}
          selectedAsset={selectedAssetIds}
          filteredAssets={unfilteredAssets}
        />
      )}

      {/* Header Section */}
      <Paper
        elevation={3}
        sx={{
          p: 0,
          mb: 4,
          mt: 2,
          borderRadius: '16px',
          overflow: 'hidden',
          background: enableDarkTheme
            ? `linear-gradient(145deg, ${alpha(
                _dark.toolBarBackground,
                0.8
              )} 0%, ${_dark.pageBackground} 100%)`
            : 'linear-gradient(145deg, #ffffff 0%, #f7f9fc 100%)',
          boxShadow: enableDarkTheme
            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
            : '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${
            enableDarkTheme ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.03)'
          }`,
        }}
      >
        {/* Header Title */}
        <Box
          sx={{
            p: 2.5,
            pb: 2,
            borderBottom: `1px solid ${
              enableDarkTheme ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'
            }`,
            background: enableDarkTheme
              ? `linear-gradient(to right, ${alpha(
                  _dark.primary,
                  0.15
                )}, ${alpha(_dark.boxBackground, 0.2)})`
              : `linear-gradient(to right, ${alpha(
                  _light.primary,
                  0.07
                )}, rgba(240,245,255,0.5))`,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              letterSpacing: '0.02em',
              color: enableDarkTheme ? 'rgba(255,255,255,0.95)' : '#334155',
            }}
          >
            <TuneIcon
              sx={{
                mr: 1.5,
                color: enableDarkTheme ? _dark.primary : _light.primary,
              }}
            />
            {t('filter_configure')}
          </Typography>
        </Box>

        <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
          <Grid container spacing={3} alignItems="stretch">
            {/* Date Range Section */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${
                    enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.05)'
                  }`,
                  backgroundColor: 'transparent',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    pb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `1px solid ${
                      enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.06)'
                        : 'rgba(0, 0, 0, 0.04)'
                    }`,
                    background: enableDarkTheme
                      ? 'rgba(0, 0, 0, 0.15)'
                      : alpha(_light.primary, 0.04),
                  }}
                >
                  <CalendarTodayIcon
                    sx={{
                      mr: 1.5,
                      color: enableDarkTheme ? _dark.primary : _light.primary,
                      fontSize: '18px',
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      fontSize: '15px',
                      color: enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.9)'
                        : 'rgba(0, 0, 0, 0.75)',
                    }}
                  >
                    {t('date_range')}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2.5,
                    pt: 2,
                    backgroundColor: enableDarkTheme
                      ? 'rgba(30, 40, 50, 0.4)'
                      : 'rgba(255, 255, 255, 0.8)',
                  }}
                >
                  <CustomDateRangePicker
                    startDate={selectedStartDate}
                    endDate={selectedEndDate}
                    onFinalDateChange={handleDateChange}
                    maxDate={dayjs()}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Tag Selection Section */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 0,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${
                    enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.05)'
                  }`,
                  backgroundColor: 'transparent',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    pb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `1px solid ${
                      enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.06)'
                        : 'rgba(0, 0, 0, 0.04)'
                    }`,
                    background: enableDarkTheme
                      ? 'rgba(0, 0, 0, 0.15)'
                      : alpha(_light.primary, 0.04),
                  }}
                >
                  <TagIcon
                    sx={{
                      mr: 1.5,
                      color: enableDarkTheme ? _dark.primary : _light.primary,
                      fontSize: '18px',
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      letterSpacing: '0.01em',
                      fontSize: '15px',
                      color: enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.9)'
                        : 'rgba(0, 0, 0, 0.75)',
                    }}
                  >
                    {t('tag_selection')}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 2.5,
                    pt: 2,
                    backgroundColor: enableDarkTheme
                      ? 'rgba(30, 40, 50, 0.4)'
                      : 'rgba(255, 255, 255, 0.8)',
                  }}
                >
                  <Autocomplete
                    disablePortal
                    options={tagsDropdown}
                    getOptionLabel={(option) => {
                      return typeof option === 'object' ? option.label : option;
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (option && value && option?.id) {
                        return option.id === value.id;
                      }
                      return false;
                    }}
                    renderOption={(props, option) => {
                      return (
                        <li {...props} key={option.id}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: palette.textPrimary,
                            }}
                          >
                            {option.label}{' '}
                            <span style={{ fontSize: '10px' }}>
                              ({option.id})
                            </span>
                          </Typography>
                        </li>
                      );
                    }}
                    onChange={handleSelect}
                    slotProps={{
                      paper: {
                        sx: {
                          backgroundColor: palette.boxBackground,
                          color: palette.textPrimary,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                          '& .MuiAutocomplete-option': {
                            padding: '10px 16px',
                            borderBottom: enableDarkTheme
                              ? '1px solid rgba(255, 255, 255, 0.1)'
                              : '1px solid rgba(0, 0, 0, 0.05)',
                            margin: '0 4px',
                            borderRadius: '4px',
                            '&:not(:last-child)': {
                              marginBottom: '2px',
                            },
                            '&:hover': {
                              backgroundColor: alpha(palette.primary, 0.1),
                            },
                            '&.Mui-focused': {
                              backgroundColor: alpha(palette.primary, 0.15),
                            },
                          },
                          '& .MuiAutocomplete-listbox': {
                            '&::-webkit-scrollbar': {
                              width: '8px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: enableDarkTheme
                                ? alpha(_dark.primary, 0.7)
                                : alpha(_light.primary, 0.5),
                              borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                              backgroundColor: 'transparent',
                            },
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <SWTextField
                        name="tagName"
                        placeholder={t('add_to_the_screen_for_comparison')}
                        TextFieldProps={{
                          ...params,
                          name: 'tagName',
                          InputProps: {
                            ...params.InputProps,
                            sx: {
                              color: palette.textPrimary,
                              borderRadius: '8px',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: enableDarkTheme
                                  ? 'rgba(255, 255, 255, 0.2)'
                                  : 'rgba(0, 0, 0, 0.15)',
                                transition: 'border-color 0.2s ease',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: enableDarkTheme
                                  ? 'rgba(255, 255, 255, 0.3)'
                                  : 'rgba(0, 0, 0, 0.25)',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: enableDarkTheme
                                    ? _dark.primary
                                    : _light.primary,
                                  boxShadow: `0 0 0 2px ${alpha(
                                    enableDarkTheme
                                      ? _dark.primary
                                      : _light.primary,
                                    0.2
                                  )}`,
                                },
                            },
                          },
                        }}
                        enableDarkTheme={enableDarkTheme}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-paper': {
                        backgroundColor: palette.boxBackground,
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                      },
                      '& .MuiAutocomplete-option': {
                        '&:hover': {
                          backgroundColor: alpha(palette.primary, 0.1),
                        },
                        '&.Mui-focused': {
                          backgroundColor: alpha(palette.primary, 0.15),
                        },
                      },
                      '& .MuiAutocomplete-listbox': {
                        '&::-webkit-scrollbar': {
                          width: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: enableDarkTheme
                            ? alpha(_dark.primary, 0.7)
                            : alpha(_light.primary, 0.5),
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                          backgroundColor: 'transparent',
                        },
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Assets Header */}
      <Header
        enableDarkTheme={enableDarkTheme}
        assets={assets}
        setShowModal={setShowModal}
        onRemoveAsset={handleRemoveAsset}
        onEditAsset={handleEditAsset}
      />

      {/* Key Metrics Section */}
      <Paper
        elevation={2}
        sx={{
          mt: 3,
          backgroundColor: palette.boxBackground,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Metrics Header */}
        <Box
          sx={{
            p: 3,
            pb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: palette.textPrimary,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {t('key_metrics')}
            <Chip
              label={`${assets?.length} Assets`}
              size="small"
              sx={{
                ml: 2,
                backgroundColor: enableDarkTheme
                  ? _dark.primary
                  : _light.primary,
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Typography>

          {/* <ButtonGroup
            variant="contained"
            sx={{
              borderRadius: '8px',
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <Button
              onClick={() => {
                handleButtonClick('table');
              }}
              sx={{
                backgroundColor:
                  metricsType === 'table' ? palette.primary : palette.sectionBackground,
                '&:hover': {
                  backgroundColor: palette.primary,
                },
                transition: 'all 0.2s ease',
                minWidth: '48px',
                px: 2,
              }}
            >
              <TableChartIcon />
            </Button>
            <Button
              onClick={() => {
                handleButtonClick('chart');
              }}
              sx={{
                backgroundColor:
                  metricsType === 'chart' ? palette.primary : palette.sectionBackground,
                '&:hover': {
                  backgroundColor: palette.primary,
                },
                transition: 'all 0.2s ease',
                minWidth: '48px',
                px: 2,
              }}
            >
              <BarChartIcon />
            </Button>
          </ButtonGroup> */}
        </Box>

        <Divider
          sx={{
            backgroundColor: enableDarkTheme
              ? _dark.borderGrey
              : 'rgba(0,0,0,0.12)',
          }}
        />

        {/* Metrics Content */}
        <Box
          sx={{
            p: 2,
          }}
        >
          <ComparisonMetrics
            metricsType={metricsType}
            enableDarkTheme={enableDarkTheme}
            assets={assets}
            assetsComparison={{ metrics: tagMetrics }}
            itemLoading={
              assetsComparison.tags.length > 0
                ? assetsComparison.tags[assetsComparison.tags.length - 1]
                    .loading
                : false
            }
            showChart={false}
            thresholds={thresholds}
            defaultThresholds={defaultThresholds}
            tagIdToLabelMap={tagIdToLabelMap}
          />

          {assetsComparison.loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200,
              }}
            >
              <SingleFadeLoader />
            </Box>
          ) : (
            <ComparisonMetrics
              metricsType={metricsType}
              enableDarkTheme={enableDarkTheme}
              assets={assets}
              assetsComparison={assetsComparison}
              tagMetrics={tagMetrics}
              thresholds={thresholds}
              defaultThresholds={defaultThresholds}
            />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default FleetComparisonNew;
